import {
  Button,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Typography,
  CircularProgress,
  InputBase,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import * as React from "react";
import PropTypes from "prop-types";

export const HeaderButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "mobile",
})(({ theme, variant, mobile }) => ({
  height: "38px",
  boxShadow: "none", // Default to no box shadow

  // Conditional styles based on the variant
  ...(variant === "contained" && {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
      boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
    },
  }),
  ...(variant === "outlined" && {
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  }),
  ...(mobile && {
    minWidth: 0,
    padding: 0,
    height: 38,
    width: 38,
    ".MuiButton-startIcon": {
      margin: 0,
    },
  }),
}));

HeaderButton.defaultProps = {
  variant: "contained",
};

export const HeaderToggleButtonGroup = styled(ToggleButtonGroup)(
  ({ theme }) => ({
    "& .MuiToggleButton-root": {
      border: "none",
      borderRadius: theme.shape.borderRadius,
    },
  }),
);

export const HeaderToggleButton = styled(ToggleButton)(({ theme }) => ({
  "&.Mui-selected": {
    backgroundColor: theme.palette.secondary.main,
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  // Gate the hover tint behind a hover-capable pointer. On touch devices the
  // :hover state "sticks" after a tap/navigation until the next scroll, which
  // made an unselected toggle look faintly highlighted on first landing.
  "@media (hover: hover)": {
    "&:hover": {
      backgroundColor: alpha(theme.palette.secondary.main, 0.3),
    },
  },
}));

export const Search = styled("div")(({ theme, highlight }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.secondary.main,
  marginRight: theme.spacing(1),
  width: "auto",
  height: "38px",
  display: "flex",
  border: highlight ? `1px solid ${theme.palette.primary.main}` : undefined,
}));

export const SearchIconWrapper = styled("div")(({ theme, highlight }) => ({
  paddingLeft: theme.spacing(1),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: highlight ? theme.palette.primary.main : undefined, // Conditional icon color
}));

export const StyledInputBase = styled(InputBase)(({ theme }) => ({
  "& .MuiInputBase-input": {
    paddingLeft: "38px",
    transition: theme.transitions.create("width"),
    fontSize: "0.875rem",
    width: 0,
    "&:focus, &:not(:placeholder-shown)": {
      width: "10ch", // Default width on focus for smaller screens
      // pr: "24px",
      [theme.breakpoints.up("md")]: {
        width: "15ch", // Larger width on focus for wider screens
      },
    },
  },
}));

// Mirrors MUI's natural link styling: a permanently-visible underline that's
// translucent at rest (so links read as tappable on mobile) and resolves to a full,
// solid underline on hover/tap. The underline inherits the text colour throughout.
export const StyledLink = styled(Link)(() => ({
  color: "inherit",
  cursor: "pointer",
  textDecoration: "underline",
  textDecorationColor: "color-mix(in srgb, currentColor 40%, transparent)",
  "&:hover": {
    textDecorationColor: "currentColor",
  },
}));

export const CircularWithLabel = (props) => {
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress variant="determinate" {...props} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="caption"
          component="div"
          sx={{ color: "text.secondary" }}
        >
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
};

CircularWithLabel.propTypes = {
  /**
   * The value of the progress indicator for the determinate variant.
   * Value between 0 and 100.
   * @default 0
   */
  value: PropTypes.number.isRequired,
};

export const RechartsTooltipContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
  padding: theme.spacing(0.5, 1),
  border: `1px solid ${theme.palette.divider}`,
}));

export const GenericRechartsTooltip = ({ active, payload, renderContent }) => {
  if (!active || !payload?.length) return null;

  return (
    <RechartsTooltipContainer>
      <Typography variant="caption" color="text.primary" sx={{ lineHeight: 1 }}>
        {renderContent(payload[0].payload)}
      </Typography>
    </RechartsTooltipContainer>
  );
};
